import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress,
  Alert, Chip, TextField, MenuItem, InputAdornment, useTheme
} from '@mui/material';
import { TrendingDown, TrendingUp, Remove, Search } from '@mui/icons-material';
import api from '../../api/axios';

// ─── Tasarım sabitleri ────────────────────────────────────────────────────────
const BRAND  = '#1A3A6B';

interface CompetitorPrice {
  id: number;
  cableFamily: string;
  spec: string;
  competitorName: string;
  listPriceTl: number | null;
  discountRate: number;
  netPriceTl: number | null;
  notes: string;
  updatedAt: string;
}

interface Meta {
  families: string[];
  competitors: string[];
}

const familyColor: Record<string, string> = {
  NAYY: '#D97706', NA2XY: '#0284C7', NA2XH: '#7C3AED',
  AER: '#B45309', AAC: '#16A34A', ACSR: '#1A3A6B'
};

const ComparisonPage: React.FC = () => {
  const theme   = useTheme();
  const BORDER  = theme.palette.divider;
  const SURFACE = theme.palette.background.paper;
  const TEXT2   = theme.palette.text.secondary;

  const [prices, setPrices] = useState<CompetitorPrice[]>([]);
  const [meta, setMeta] = useState<Meta>({ families: [], competitors: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterFamily, setFilterFamily] = useState('');
  const [filterSpec, setFilterSpec] = useState('');
  const [search, setSearch] = useState('');
  const [extraDiscounts, setExtraDiscounts] = useState<Record<string, number>>({});

  useEffect(() => {
    Promise.all([
      api.get('/competitor-prices'),
      api.get('/competitor-prices/meta'),
    ]).then(([pricesRes, metaRes]) => {
      setPrices(pricesRes.data);
      setMeta(metaRes.data);
      if (metaRes.data.families?.length > 0) setFilterFamily(metaRes.data.families[0]);
    }).catch(() => setError('Veriler yüklenemedi'))
      .finally(() => setLoading(false));
  }, []);

  const specs = useMemo(() => {
    const s = prices.filter(p => p.cableFamily === filterFamily).map(p => p.spec);
    return Array.from(new Set(s)).sort((a, b) => {
      const [fa, sa] = a.split('x').map(Number);
      const [fb, sb] = b.split('x').map(Number);
      return fa !== fb ? fa - fb : sa - sb;
    });
  }, [prices, filterFamily]);

  const tableData = useMemo(() => {
    const filtered = prices.filter(p =>
      p.cableFamily === filterFamily &&
      (filterSpec === '' || p.spec === filterSpec) &&
      (!search || p.spec.includes(search))
    );
    const specMap: Record<string, Record<string, CompetitorPrice>> = {};
    filtered.forEach(p => {
      if (!specMap[p.spec]) specMap[p.spec] = {};
      specMap[p.spec][p.competitorName] = p;
    });
    return Object.entries(specMap).sort(([a], [b]) => {
      const [fa, sa] = a.split('x').map(Number);
      const [fb, sb] = b.split('x').map(Number);
      return fa !== fb ? fa - fb : sa - sb;
    });
  }, [prices, filterFamily, filterSpec, search]);

  const competitors = meta.competitors.filter(c => c !== 'KLC');
  const allComps = ['KLC', ...competitors];

  const calcNet = (p: CompetitorPrice | undefined, compName: string): number | null => {
    if (!p || !p.listPriceTl) return p?.netPriceTl ?? null;
    const extraDisc = extraDiscounts[compName] || 0;
    const totalDisc = (p.discountRate || 0) + extraDisc / 100;
    return parseFloat((p.listPriceTl * (1 - totalDisc)).toFixed(2));
  };

  const fmtPrice = (p: CompetitorPrice | undefined, compName: string) => {
    if (!p) return null;
    const extraDisc = extraDiscounts[compName] || 0;
    const totalDisc = (p.discountRate || 0) + extraDisc / 100;
    return {
      net: calcNet(p, compName),
      list: p.listPriceTl,
      disc: totalDisc,
      extraDisc,
    };
  };

  const getDiff = (klcNet: number | null | undefined, compNet: number | null | undefined) => {
    if (!klcNet || !compNet) return null;
    return ((klcNet - compNet) / compNet) * 100;
  };

  const stats = useMemo(() => {
    let cheaper = 0, expensive = 0, equal = 0;
    tableData.forEach(([_, compMap]) => {
      const klcNet = calcNet(compMap['KLC'], 'KLC');
      competitors.forEach(comp => {
        const d = getDiff(klcNet, calcNet(compMap[comp], comp));
        if (d === null) return;
        if (d < -0.5) cheaper++;
        else if (d > 0.5) expensive++;
        else equal++;
      });
    });
    return { cheaper, expensive, equal };
  }, [tableData, competitors, extraDiscounts]); // eslint-disable-line

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress sx={{ color: BRAND }} /></Box>;
  if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;

  return (
    <Box sx={{ p: 3, bgcolor: theme.palette.background.default, minHeight: '100vh' }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2 }}>
            Rakip Fiyat Karşılaştırması
          </Typography>
          <Typography sx={{ fontSize: '13px', color: TEXT2, mt: 0.5 }}>
            KLC Kablo vs rakip firma fiyatları · TL/m bazında
          </Typography>
        </Box>
        {/* Özet istatistik */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {[
            { label: 'KLC Ucuz', count: stats.cheaper, color: '#16A34A', bg: '#f0fdf4', border: '#bbf7d0' },
            { label: 'KLC Pahalı', count: stats.expensive, color: '#DC2626', bg: '#fef2f2', border: '#fecaca' },
            { label: 'Eşit', count: stats.equal, color: '#64748B', bg: '#f8fafc', border: '#e2e8f0' },
          ].map(({ label, count, color, bg, border }) => (
            <Box key={label} sx={{
              px: 2, py: 1, bgcolor: bg, borderRadius: '10px',
              border: `1px solid ${border}`, textAlign: 'center', minWidth: 80
            }}>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1 }}>{count}</Typography>
              <Typography sx={{ fontSize: '10px', fontWeight: 700, color,
                letterSpacing: '0.05em', textTransform: 'uppercase', mt: 0.3 }}>{label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Filtreler ── */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: `1px solid ${BORDER}`, borderRadius: '10px', bgcolor: SURFACE }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <TextField
            placeholder="Kesit ara... (örn: 3x95)" value={search}
            onChange={e => setSearch(e.target.value)} size="small"
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: TEXT2 }} /></InputAdornment>,
              sx: { borderRadius: '8px', fontSize: '13px', minWidth: 200 },
            }}
            sx={{ '& fieldset': { borderColor: BORDER } }}
          />

          <Box sx={{ width: 1, height: 24, bgcolor: BORDER, mx: 0.5 }} />

          <Typography sx={{ fontSize: '12px', color: TEXT2, fontWeight: 600 }}>Aile:</Typography>
          {meta.families.map(f => (
            <Chip key={f} label={f} size="small"
                  onClick={() => { setFilterFamily(f); setFilterSpec(''); setSearch(''); }}
                  sx={{
                    height: 22, fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                    bgcolor: filterFamily === f ? familyColor[f] || BRAND : 'transparent',
                    color: filterFamily === f ? 'white' : familyColor[f] || BRAND,
                    border: `1.5px solid ${familyColor[f] || BRAND}`,
                    '&:hover': { opacity: 0.85 }
                  }} />
          ))}

          <TextField select size="small" label="Kesit" value={filterSpec}
                     onChange={e => setFilterSpec(e.target.value)}
                     sx={{ minWidth: 130, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
            <MenuItem value="">Tümü</MenuItem>
            {specs.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>

          <Typography sx={{ fontSize: '12px', color: TEXT2, ml: 'auto' }}>
            <Box component="span" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>{tableData.length}</Box> ürün
          </Typography>
        </Box>

        {/* İskonto girişi */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
                   pt: 2, borderTop: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: TEXT2,
                             letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Ek İskonto (%):
          </Typography>
          {allComps.map(comp => (
            <Box key={comp} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{
                fontSize: '11px', fontWeight: 700,
                color: comp === 'KLC' ? BRAND : TEXT2
              }}>
                {comp === 'KLC' ? '⭐ KLC' : comp}
              </Typography>
              <TextField
                type="number" size="small"
                value={extraDiscounts[comp] ?? ''}
                onChange={e => {
                  const val = parseFloat(e.target.value) || 0;
                  setExtraDiscounts(prev => ({ ...prev, [comp]: val }));
                }}
                placeholder="0"
                inputProps={{ min: 0, max: 100, step: 0.5 }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={{ width: 90, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Box>
          ))}
          {Object.values(extraDiscounts).some(v => v > 0) && (
            <Chip label="İskonto uygulandı" size="small"
                  sx={{ height: 22, bgcolor: '#FEF3C7', color: '#B45309', fontWeight: 700, fontSize: '11px' }}
                  onDelete={() => setExtraDiscounts({})} />
          )}
        </Box>
      </Paper>

      {/* ── Legend ── */}
      <Box sx={{ display: 'flex', gap: 2.5, mb: 2, flexWrap: 'wrap' }}>
        {[
          { icon: <TrendingDown sx={{ color: '#16A34A', fontSize: 14 }} />, label: 'KLC rakipten ucuz', color: '#16A34A' },
          { icon: <TrendingUp sx={{ color: '#DC2626', fontSize: 14 }} />, label: 'KLC rakipten pahalı', color: '#DC2626' },
          { icon: <Remove sx={{ color: '#64748B', fontSize: 14 }} />, label: 'Eşit / Veri yok', color: '#64748B' },
        ].map(({ icon, label, color }) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {icon}
            <Typography sx={{ fontSize: '11px', color, fontWeight: 600 }}>{label}</Typography>
          </Box>
        ))}
      </Box>

      {/* ── Tablo ── */}
      <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden', bgcolor: SURFACE }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 420px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell style={{ background: '#f8f9fc', paddingTop: 12, paddingBottom: 12,
                  fontSize: 11, fontWeight: 600, color: '#8a92a6',
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                  borderBottom: '1px solid #e8eaf0', minWidth: 100 }}>
                  Kesit
                </TableCell>
                {allComps.map(comp => (
                  <TableCell key={comp} align="center"
                    style={{
                      paddingTop: 12, paddingBottom: 12,
                      fontSize: 11, fontWeight: 700, color: comp === 'KLC' ? BRAND : '#8a92a6',
                      textTransform: 'uppercase', letterSpacing: '0.05em',
                      backgroundColor: comp === 'KLC' ? '#eff6ff' : '#f8f9fc',
                      borderBottom: comp === 'KLC' ? `2px solid ${BRAND}` : '1px solid #e8eaf0',
                      minWidth: 130
                    }}>
                    <Box>
                      {comp === 'KLC' ? '⭐ KLC' : comp}
                      {(extraDiscounts[comp] ?? 0) > 0 && (
                        <Chip label={`-%${extraDiscounts[comp]}`} size="small"
                              sx={{ ml: 0.5, height: 16, fontSize: '9px', bgcolor: '#F59E0B', color: 'white' }} />
                      )}
                    </Box>
                  </TableCell>
                ))}
                {competitors.map(comp => (
                  <TableCell key={`diff-${comp}`} align="center"
                    style={{ background: '#f8f9fc', paddingTop: 12, paddingBottom: 12,
                      fontSize: 10, fontWeight: 600, color: '#8a92a6',
                      textTransform: 'uppercase', letterSpacing: '0.05em',
                      borderBottom: '1px solid #e8eaf0', minWidth: 100 }}>
                    vs {comp}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={allComps.length + competitors.length + 1}
                             align="center" sx={{ py: 8, color: TEXT2 }}>
                    <Typography sx={{ fontSize: '14px' }}>Veri bulunamadı</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tableData.map(([spec, compMap]) => {
                  const klcNet = calcNet(compMap['KLC'], 'KLC');
                  return (
                    <TableRow key={spec}
                              sx={{
                                transition: 'background .1s',
                                '& td': { borderBottom: '1px solid #e8eaf0', py: 1 },
                                '&:hover td': { bgcolor: '#fafbfe !important' },
                              }}>
                      <TableCell>
                        <Typography sx={{
                          fontSize: '13px', fontWeight: 700, color: BRAND,
                          fontFamily: 'monospace'
                        }}>
                          {filterFamily} {spec}
                        </Typography>
                      </TableCell>
                      {allComps.map(comp => {
                        const d = fmtPrice(compMap[comp], comp);
                        const isKlc = comp === 'KLC';
                        return (
                          <TableCell key={comp} align="center"
                                     sx={{ bgcolor: isKlc ? '#EFF6FF' : 'transparent' }}>
                            {d?.net != null ? (
                              <Box>
                                <Typography sx={{
                                  fontSize: '14px', fontWeight: 700,
                                  fontFamily: 'monospace',
                                  color: isKlc ? BRAND : theme.palette.text.primary
                                }}>
                                  {d.net.toFixed(2)} ₺
                                </Typography>
                                {d.list != null && d.list !== d.net && (
                                  <Typography sx={{
                                    fontSize: '10px', color: TEXT2,
                                    textDecoration: 'line-through', display: 'block',
                                    fontFamily: 'monospace'
                                  }}>
                                    {d.list.toFixed(2)} ₺
                                  </Typography>
                                )}
                                {d.disc != null && d.disc > 0 && (
                                  <Chip label={`-%${(d.disc * 100).toFixed(0)}`}
                                        size="small"
                                        sx={{ height: 16, fontSize: '9px', mt: 0.3,
                                              bgcolor: '#FEF3C7', color: '#B45309', fontWeight: 700 }} />
                                )}
                              </Box>
                            ) : (
                              <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Typography>
                            )}
                          </TableCell>
                        );
                      })}
                      {competitors.map(comp => {
                        const compNet = calcNet(compMap[comp], comp);
                        const diff = getDiff(klcNet, compNet);
                        return (
                          <TableCell key={`diff-${comp}`} align="center">
                            {diff !== null ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                {diff < -0.5 ? (
                                  <TrendingDown sx={{ color: '#16A34A', fontSize: 14 }} />
                                ) : diff > 0.5 ? (
                                  <TrendingUp sx={{ color: '#DC2626', fontSize: 14 }} />
                                ) : (
                                  <Remove sx={{ color: '#64748B', fontSize: 14 }} />
                                )}
                                <Typography sx={{
                                  fontSize: '12px', fontWeight: 700,
                                  fontFamily: 'monospace',
                                  color: diff < -0.5 ? '#16A34A' : diff > 0.5 ? '#DC2626' : '#64748B'
                                }}>
                                  {diff > 0 ? '+' : ''}{diff.toFixed(1)}%
                                </Typography>
                              </Box>
                            ) : (
                              <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ComparisonPage;
